import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { addressesAPI } from '../api/address';

import { getNearestBranch } from '../libs/nearestBranch';

import { RootState } from '../../../store'; // update with actual path
import { homeAPI } from '../../home/api/homeAPI';
import { menuListAdapter } from '../../home/libs/menuAdapter';
import {
  setAvailabilityPending,
  setBasketFromServer,
  updateBasketWithNewMenu,
} from '../../../store/slices/basketSlice';
import { applyBranchAvailability } from '../../pickup/services/checkBranchAvailability';
import { IProductInfo } from '../../home/types/Menu';
import { userLocation } from '../../../store/selectors/location';
import {
  userAddressesList,
  userSavedAddresses,
} from '../../../store/selectors/userAddresses';
import { isPickupSelected } from '../../../store/selectors/fulfillment';
import { FORCED_BRANCH_ID } from '../../../lib/testBranch';

export const useGetNearesBranch = () => {
  const { latitude: lat, longitude: lng, hasFix } = useSelector(userLocation);
  const addresses = useSelector(userAddressesList);
  const selectedAddressId = useSelector(userSavedAddresses);
  const isPickup = useSelector(isPickupSelected);
  const pickupBranchId = useSelector(
    (state: RootState) => Number(state.pickup?.branch?._id) || 0
  );

  // Get coordinates from saved address if available, otherwise use current location
  const { finalLat, finalLng } = useMemo(() => {
    if (selectedAddressId && addresses.length > 0) {
      const savedAddress = addresses.find(
        addr => addr._id === selectedAddressId
      );
      if (savedAddress) {
        return {
          finalLat: savedAddress.location.lat,
          finalLng: savedAddress.location.lon,
        };
      }
    }
    return { finalLat: lat, finalLng: lng };
  }, [selectedAddressId, addresses, lat, lng]);

  const dispatch = useDispatch();

  const basketItems = useSelector((state: RootState) => state.basket.items);
  const basketItemsRef = useRef(basketItems);
  basketItemsRef.current = basketItems;

  const initData = useMemo(() => ({ nearest: getNearestBranch() }), []);
  const { data = initData, isLoading } = useQuery({
    queryKey: ['nearest-branch', finalLat, finalLng],
    queryFn: async () => {
      const res = await addressesAPI.getNearBranch(finalLat, finalLng);

      if (!res?.data?.success && res?.data?.message) {
        window.Telegram?.WebApp?.showPopup({
          message: res.data.message,
        });
      }

      return res;
    },
    select: data => ({
      nearest: getNearestBranch(data.data.branch),
    }),
    refetchOnMount: false,
  });

  // Вне прода филиал задан константой: подменяем его сразу после запроса,
  // чтобы и меню, и корзина, и подборки на главной смотрели на один и тот же
  // branch_id.
  const nearest = useMemo(
    () =>
      FORCED_BRANCH_ID
        ? { ...data.nearest, branch_id: FORCED_BRANCH_ID }
        : data.nearest,
    [data.nearest]
  );

  useEffect(() => {
    // Whichever branch will actually fulfil the order decides what's in stock:
    // the one picked on the map for pickup, the nearest one for delivery.
    // Re-running on every mode switch is what keeps the cart honest when the
    // user toggles delivery <-> pickup without revisiting the map.
    const branchId = isPickup ? pickupBranchId : nearest?.branch_id;

    if (!branchId || basketItems.length === 0) return;

    // Пока сверка в пути, корзина показывает шиммер вместо «нет в наличии»
    // (см. isAvailabilityPending). Снимаем флаг на любом исходе — иначе
    // шиммер останется навсегда.
    dispatch(setAvailabilityPending(true));

    homeAPI
      .getMenus(branchId)
      .then(res => {
        const adaptedMenu = menuListAdapter(res?.data);

        const allProducts: IProductInfo[] = adaptedMenu?.flatMap(
          item => item.menu
        );

        if (!allProducts?.length) {
          dispatch(setAvailabilityPending(false));
          return;
        }

        if (isPickup) {
          // Authoritative: persist so the cart's storage re-read can't undo it.
          // Read the items through a ref — dispatching replaces the array, and
          // depending on it here would retrigger this effect forever.
          dispatch(
            setBasketFromServer(
              applyBranchAvailability(basketItemsRef.current, allProducts)
            )
          );
          dispatch(setAvailabilityPending(false));
          return;
        }

        dispatch(updateBasketWithNewMenu(allProducts));
      })
      .catch(() => dispatch(setAvailabilityPending(false)));
  }, [
    nearest?.branch_id,
    pickupBranchId,
    basketItems.length,
    dispatch,
    isPickup,
  ]);

  // Без адреса и без geo-фикса координаты — это дефолтный центр Ташкента, и
  // "ближайший" филиал по ним получается случайным. Такой branch_id нельзя
  // выдавать за выбор пользователя: он годится для меню, но не для подборок,
  // которые бэк фильтрует по филиалу.
  const hasSelectedAddress = Boolean(
    selectedAddressId && addresses.some(item => item._id === selectedAddressId)
  );
  const isBranchResolved =
    // Прибитый филиал — это осознанный выбор, а не «угадали по центру города»:
    // иначе главная не запросит баннеры и сторис для него.
    Boolean(FORCED_BRANCH_ID) ||
    hasSelectedAddress ||
    hasFix ||
    (isPickup && pickupBranchId > 0);

  return {
    ...data,
    nearest,
    isBranchResolved,
    isLoading,
  };
};
